import { useMediaQuery, useMutationObserver, watchImmediate } from "@vueuse/core";
import { computed, effectScope, ref } from "vue";
import { useBooleanModifiers } from "./useModifiers";

function getPropertyValue(propery) {
  return window.getComputedStyle(document.documentElement).getPropertyValue(propery);
}

/**
 * Create a callback function to be called when the breakpoint changes
 * and update the receiver ref with the new value.
 *
 * It also watches the media query to update the receiver ref when the
 * media query changes.
 *
 * It keeps track of the watcher to unwatch it when the breakpoint changes.
 *
 * @param {import("vue").Ref} receiver
 * @param {import("vue").Ref} breakpoint
 * @returns Function
 */
function createOnChangeBreakpointCallback(receiver, breakpoint) {
  let unwatch;

  return () => {
    unwatch?.();

    const watched = useMediaQuery(`(min-width: ${breakpoint.value})`);

    unwatch = watchImmediate(watched, (value) => {
      receiver.value = value;
    });
  };
}

// The breakpoint reference tokens (`--sp-ref-breakpoint-*`) are document-global and
// identical for every custom element instance, yet resolving them — and their media
// query watchers — per instance forces a synchronous style recalc on every mount
// (layout thrashing when many elements mount at once, e.g. the admin icon library).
// Resolve the state once and share it across all callers. It lives in a detached
// effect scope so its watchers/observer are not disposed when the first consuming
// component unmounts.
let sharedBreakpointState;
let sharedBreakpointScope;

/**
 * Builds the shared breakpoint state. Call only via {@link useBreakpoints}, which
 * memoizes the result inside a detached effect scope.
 *
 * @returns {{
 *  smBreakpoint: import("vue").Ref<string>,
 *  mdBreakpoint: import("vue").Ref<string>,
 *  lgBreakpoint: import("vue").Ref<string>,
 *  xlBreakpoint: import("vue").Ref<string>,
 *  isSmScreen: import("vue").ComputedRef<boolean>,
 *  isMdScreen: import("vue").ComputedRef<boolean>,
 *  isLgScreen: import("vue").ComputedRef<boolean>,
 *  isXlScreen: import("vue").ComputedRef<boolean>,
 *  isGtSmScreen: import("vue").Ref<boolean>,
 *  isGtMdScreen: import("vue").Ref<boolean>,
 *  isGtLgScreen: import("vue").Ref<boolean>,
 *  isGtXlScreen: import("vue").Ref<boolean>,
 *  modifiers: import("vue").ComputedRef<string[]>,
 * }}
 */
function buildBreakpointState() {
  const smBreakpoint = ref("");
  const isGtSmScreen = ref(false);
  watchImmediate(smBreakpoint, createOnChangeBreakpointCallback(isGtSmScreen, smBreakpoint));

  const mdBreakpoint = ref("");
  const isGtMdScreen = ref(false);
  watchImmediate(mdBreakpoint, createOnChangeBreakpointCallback(isGtMdScreen, mdBreakpoint));

  const lgBreakpoint = ref("");
  const isGtLgScreen = ref(false);
  watchImmediate(lgBreakpoint, createOnChangeBreakpointCallback(isGtLgScreen, lgBreakpoint));

  const xlBreakpoint = ref("");
  const isGtXlScreen = ref(false);
  watchImmediate(xlBreakpoint, createOnChangeBreakpointCallback(isGtXlScreen, xlBreakpoint));

  const { stop } = useMutationObserver(document.documentElement, setBreakpointValues, {
    subtree: true,
    attributes: true,
    attributeFilter: ["class", "style"],
  });

  /**
   * Set the breakpoint values from the CSS variables.
   */
  function setBreakpointValues() {
    smBreakpoint.value = getPropertyValue("--sp-ref-breakpoint-sm");
    mdBreakpoint.value = getPropertyValue("--sp-ref-breakpoint-md");
    lgBreakpoint.value = getPropertyValue("--sp-ref-breakpoint-lg");
    xlBreakpoint.value = getPropertyValue("--sp-ref-breakpoint-xl");

    // Stop the observer when the breakpoints are set, this is only needed once
    if (smBreakpoint.value && mdBreakpoint.value && lgBreakpoint.value && xlBreakpoint.value) {
      stop?.();
    }
  }

  const isSmScreen = computed(() => isGtSmScreen.value === false);
  const isMdScreen = computed(() => isGtSmScreen.value === true && isGtMdScreen.value === false);
  const isLgScreen = computed(() => isGtMdScreen.value === true && isGtLgScreen.value === false);
  const isXlScreen = computed(() => isGtXlScreen.value === true);

  // Create a reactive object for useBooleanModifiers using getters to maintain reactivity to the computed refs
  const breakpointFlags = {
    get isSmScreen() {
      return isSmScreen.value;
    },
    get isMdScreen() {
      return isMdScreen.value;
    },
    get isLgScreen() {
      return isLgScreen.value;
    },
    get isXlScreen() {
      return isXlScreen.value;
    },
  };

  // min-width (>=) flags: ["--gt-sm-screen", "--gt-md-screen", "--gt-lg-screen", "--gt-xl-screen"]
  const gtFlags = {
    get gtSmScreen() {
      return isGtSmScreen.value;
    },
    get gtMdScreen() {
      return isGtMdScreen.value;
    },
    get gtLgScreen() {
      return isGtLgScreen.value;
    },
    get gtXlScreen() {
      return isGtXlScreen.value;
    },
  };

  // max-width (<) flags: ["--lt-sm-screen", "--lt-md-screen", "--lt-lg-screen", "--lt-xl-screen"]
  const ltFlags = {
    get ltSmScreen() {
      return !isGtSmScreen.value;
    },
    get ltMdScreen() {
      return !isGtMdScreen.value;
    },
    get ltLgScreen() {
      return !isGtLgScreen.value;
    },
    get ltXlScreen() {
      return !isGtXlScreen.value;
    },
  };

  // Use useBooleanModifiers to generate the CSS modifiers ("--is-sm-screen", "--is-md-screen", "--is-lg-screen", "--is-xl-screen")
  const { booleanModifiers: breakpointModifiers } = useBooleanModifiers(
    breakpointFlags,
    "isSmScreen",
    "isMdScreen",
    "isLgScreen",
    "isXlScreen",
  );
  const { booleanModifiers: gtModifiers } = useBooleanModifiers(
    gtFlags,
    "gtSmScreen",
    "gtMdScreen",
    "gtLgScreen",
    "gtXlScreen",
  );
  const { booleanModifiers: ltModifiers } = useBooleanModifiers(
    ltFlags,
    "ltSmScreen",
    "ltMdScreen",
    "ltLgScreen",
    "ltXlScreen",
  );

  // Detect the breakpoints when the composable is created, this speeds up the initial detection
  setBreakpointValues();

  return {
    smBreakpoint,
    mdBreakpoint,
    lgBreakpoint,
    xlBreakpoint,
    isSmScreen,
    isMdScreen,
    isLgScreen,
    isXlScreen,
    isGtSmScreen,
    isGtMdScreen,
    isGtLgScreen,
    isGtXlScreen,
    breakpointModifiers,
    gtModifiers,
    ltModifiers,
  };
}

/**
 * A composable to get the current breakpoints and whether the screen is greater than
 * a specific breakpoint.
 *
 * Returns a process-wide singleton: the breakpoint token resolution and media query
 * watchers are created once, inside a detached effect scope (so they survive an
 * individual component unmounting), and shared by every caller.
 */
export function useBreakpoints() {
  if (!sharedBreakpointState) {
    sharedBreakpointScope = effectScope(true);
    sharedBreakpointState = sharedBreakpointScope.run(buildBreakpointState);
  }

  return sharedBreakpointState;
}

/**
 * Disposes the shared breakpoint singleton so the next {@link useBreakpoints} call
 * rebuilds it. Test-only: specs stub `matchMedia` per case and need a fresh read.
 */
export function resetBreakpointState() {
  sharedBreakpointScope?.stop();
  sharedBreakpointScope = undefined;
  sharedBreakpointState = undefined;
}
